import { resizeImage } from "@rapid/utils/imageUtils";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";

// --- Types ---

export interface BatchJob {
    id: string;
    owner: string;
    jobId: string;
    displayName: string;
    creationTimestamp: any;
    error: boolean;
    isTerminal: boolean;
    lastNotification?: any;
    jobDefinition: { type: string; jobInput: { batchId: string } };
}

type JobStatus = "processing" | "downloading" | "done" | "error";

export function getJobStatus(job: BatchJob): JobStatus {
    if (job.error) return "error";
    if (job.isTerminal) return "done";
    const notification = job.lastNotification;
    if (
        typeof notification === "object" &&
        notification !== null &&
        notification.underlyingStageStatus === "downloading"
    ) {
        return "downloading";
    }
    return "processing";
}

export function getJobFileCount(job: BatchJob): number | null {
    const notification = job.lastNotification;
    if (typeof notification === "object" && notification?.totalTasks) {
        return notification.totalTasks;
    }
    return null;
}

export function getJobExportBatchId(job: BatchJob): string | null {
    const notification = job.lastNotification;
    if (
        typeof notification === "object" &&
        notification !== null &&
        notification.type === "job-completed-notification-v1" &&
        Array.isArray(notification.resultsBatches) &&
        notification.resultsBatches.length > 0
    ) {
        return notification.resultsBatches[0];
    }
    return null;
}

export interface PartMetadata {
    batchId: string;
    partName: string;
    partType?: string;
    partDescription?: string;
    contentType: string;
    nestedContentType?: string;
}

export interface FileMetadata {
    downloadURL: string;
    fileName: string;
    partName?: string;
    contentType?: string;
    nestedContentType?: string;
}

export async function fetchBatchParts(
    workspaceId: string,
    exportBatchId: string
): Promise<PartMetadata[]> {
    const response = await fetch(
        `/query/data-staging/${workspaceId}/batches/${exportBatchId}/parts`
    );
    if (response.status === 404) {
        throw new Error(
            "Export batch data could not be found - it either doesn't exist or has expired"
        );
    }
    if (!response.ok) {
        throw new Error(`Failed to list job export parts: ${response.statusText}`);
    }
    const data: { batchParts: Array<Omit<PartMetadata, "batchId">> } = JSON.parse(
        await response.text()
    );
    return data.batchParts.map((part) => ({ ...part, batchId: exportBatchId }));
}

export async function fetchPartFiles(
    workspaceId: string,
    batchId: string,
    partName: string,
    pageSize: number = 100,
    nextPageToken: string | null = null
): Promise<{ filesMetadata: FileMetadata[]; nextPageToken?: string }> {
    const queryParams: Record<string, string> = { pageSize: String(pageSize), partName };
    if (nextPageToken) {
        queryParams.nextPageToken = nextPageToken;
    }
    const response = await fetch(
        `/query/data-staging/${workspaceId}/batches/${batchId}/list?${new URLSearchParams(queryParams)}`
    );
    if (!response.ok) {
        throw new Error(`Failed to fetch download URLs: ${response.statusText}`);
    }
    const result: { filesMetadata?: FileMetadata[]; nextPageToken?: string } = JSON.parse(
        await response.text()
    );
    return { filesMetadata: result.filesMetadata ?? [], nextPageToken: result.nextPageToken };
}

// --- Upload promise pool ---

interface FileProgressCallback {
    (file: File, progress: number): void;
}

interface UploadFunction<T> {
    (file: File, onFileProgress?: FileProgressCallback): Promise<T>;
}

interface UploadResult<T> {
    success: T[];
    errors: { file: File; error: Error }[];
}

function uploadPromisePool<T>(
    files: File[],
    uploadFn: UploadFunction<T>,
    concurrentLimit: number,
    onProgress?: (completed: number, total: number) => void
): Promise<UploadResult<T>> {
    const pool = new Set<Promise<void>>();
    const queue = [...files];
    const successResults: T[] = [];
    const errorResults: { file: File; error: Error }[] = [];
    let totalBytes = 0;
    let completedBytes = 0;
    const activeUploads = new Map<File, number>();

    files.forEach((file) => {
        totalBytes += file.size;
        activeUploads.set(file, 0);
    });

    onProgress?.(0, totalBytes);

    const updateTotalProgress = () => {
        let inProgressBytes = 0;
        activeUploads.forEach((progress, file) => {
            inProgressBytes += (file.size * progress) / 100;
        });
        onProgress?.(completedBytes + inProgressBytes, totalBytes);
    };

    return new Promise((resolve) => {
        function next() {
            if (queue.length === 0 && pool.size === 0) {
                resolve({ success: successResults, errors: errorResults });
                return;
            }

            while (queue.length > 0 && pool.size < concurrentLimit) {
                const item = queue.shift()!;
                const promise = uploadFn(item, (file: File, progress: number) => {
                    activeUploads.set(file, progress);
                    updateTotalProgress();
                })
                    .then((result) => {
                        successResults.push(result);
                        completedBytes += item.size;
                        activeUploads.delete(item);
                        updateTotalProgress();
                    })
                    .catch((error) => {
                        errorResults.push({ file: item, error });
                        completedBytes += item.size;
                        activeUploads.delete(item);
                        updateTotalProgress();
                    })
                    .finally(() => {
                        pool.delete(promise);
                        next();
                    });

                pool.add(promise);
            }
        }

        next();
    });
}

// --- Upload functions ---

export class UploadError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.name = "UploadError";
        this.status = status;
    }
}

export function getUploadErrorReason(error: Error): string {
    if (error instanceof UploadError && error.status === 413) return "File too large";
    if (error instanceof UploadError && error.status === 401) return "Unauthorized";
    if (error.message === "Network error occurred") return "Network error";
    return "Upload failed";
}

const IMAGE_COMPRESS_THRESHOLD = 10 * 1024 * 1024; // 10MB
const IMAGE_COMPRESS_MAX_DIM = 4096;

async function compressImageIfNeeded(file: File): Promise<File> {
    if (!file.type.startsWith("image/") || file.size <= IMAGE_COMPRESS_THRESHOLD) {
        return file;
    }

    let compressed = await resizeImage(
        file,
        IMAGE_COMPRESS_MAX_DIM,
        IMAGE_COMPRESS_MAX_DIM,
        0.85,
        "image/jpeg"
    );

    if (compressed.size > IMAGE_COMPRESS_THRESHOLD) {
        compressed = await resizeImage(
            file,
            IMAGE_COMPRESS_MAX_DIM,
            IMAGE_COMPRESS_MAX_DIM,
            0.7,
            "image/jpeg"
        );
    }

    return compressed;
}

function uploadImageToBatch(
    workspaceId: string,
    file: File,
    batchId: string,
    onFileProgress?: FileProgressCallback
): Promise<any> {
    const formData = new FormData();
    formData.append(file.name, file);

    const params = new URLSearchParams({ fileName: file.name });

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
            "POST",
            `/query/batch-processing/upload-image/${workspaceId}/${batchId}?${params}`
        );
        xhr.setRequestHeader("Accept", "multipart/form-data");

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onFileProgress?.(file, (event.loaded / event.total) * 100);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch {
                    reject(new Error("Invalid JSON response"));
                }
            } else {
                reject(new UploadError(`Failed to upload image: ${xhr.statusText}`, xhr.status));
            }
        };

        xhr.onerror = () => reject(new Error("Network error occurred"));
        xhr.send(formData);
    });
}

async function uploadVideoToBatch(
    workspaceId: string,
    file: File,
    batchId: string,
    onFileProgress?: FileProgressCallback
): Promise<any> {
    const params = new URLSearchParams({ fileName: file.name });

    const response = await fetch(
        `/query/batch-processing/upload-video/${workspaceId}/${batchId}?${params}`,
        {
            method: "POST",
            headers: { "Accept": "application/json", "Content-Type": "application/json" }
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.statusText}`);
    }

    const result = await response.json();
    const { uploadURL, extensionHeaders } = result.signedURLDetails;

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadURL);

        Object.entries(extensionHeaders).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value as string);
        });

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onFileProgress?.(file, (event.loaded / event.total) * 100);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(result);
            } else {
                reject(new Error(`Failed to upload video: ${xhr.statusText}`));
            }
        };

        xhr.onerror = () => reject(new Error("Network error occurred"));
        xhr.send(file);
    });
}

// --- Public API ---

export async function uploadFilesToBatch(
    workspaceId: string,
    files: File[],
    batchId: string,
    jobType: "images" | "videos",
    onProgress?: (completed: number, total: number) => void
): Promise<{ successCount: number; failedFiles: { file: File; error: Error }[] }> {
    const filesToUpload =
        jobType === "images"
            ? await Promise.all(files.map((f) => compressImageIfNeeded(f)))
            : files;

    const uploadFn =
        jobType === "images"
            ? (file: File, onFileProgress?: FileProgressCallback) =>
                  uploadImageToBatch(workspaceId, file, batchId, onFileProgress)
            : (file: File, onFileProgress?: FileProgressCallback) =>
                  uploadVideoToBatch(workspaceId, file, batchId, onFileProgress);

    const result = await uploadPromisePool(filesToUpload, uploadFn, 5, onProgress);
    return { successCount: result.success.length, failedFiles: result.errors };
}

export async function triggerBatchJob(
    workspaceId: string,
    params: {
        apiKey: string;
        jobId: string;
        jobName: string;
        jobType: "images" | "videos";
        batchId: string;
        workspaceUrl: string;
        workflowUrl: string;
    }
): Promise<string> {
    const jobConfig = {
        type:
            params.jobType === "images"
                ? "simple-image-processing-v1"
                : "simple-video-processing-v1",
        jobInput: { type: "staging-batch-input-v1", batchId: params.batchId },
        computeConfiguration: {
            type: "compute-configuration-v2",
            machineType: "gpu",
            workersPerMachine: 1
        },
        processingSpecification: {
            type: "workflows-processing-specification-v1",
            workspace: params.workspaceUrl,
            workflowId: params.workflowUrl,
            imageInputName: "image",
            persistImagesOutputs: false,
            workflowParameters: {},
            aggregationFormat: "csv"
        }
    };

    const urlParams = new URLSearchParams({ name: params.jobName, api_key: params.apiKey });

    const response = await fetch(
        `/query/batch-processing/trigger-job/${workspaceId}/${params.jobId}?${urlParams}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Api-Key": params.apiKey
            },
            body: JSON.stringify(jobConfig)
        }
    );

    if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        const errorMessage = errorPayload?.error?.message || response.statusText;
        throw new Error(`Failed to create batch job: ${errorMessage}`);
    }

    return params.jobId;
}

export function subscribeToJobs(
    workspaceId: string,
    onUpdate: (jobs: BatchJob[]) => void,
    database: DatabaseAdapter,
    maxResults: number = 50
): () => void {
    return database.onQuerySnapshot(
        "batch_processing_jobs",
        {
            where: [
                ["owner", "==", workspaceId],
                [
                    "jobDefinition.type",
                    "in",
                    ["simple-image-processing-v1", "simple-video-processing-v1"]
                ]
            ],
            orderBy: ["creationTimestamp", "desc"],
            limit: maxResults
        },
        (docs) => {
            const jobs = docs.map((d) => ({ ...d.data(), id: d.id }) as BatchJob);
            onUpdate(jobs);
        }
    );
}
